import React, { Component } from 'react';
import { TaxonomyElement, Unit } from '../../../api/models/TaxonomyElement';
import { Radio, FormControlLabel, RadioGroup, Grid, Typography, FormControl, CircularProgress, InputLabel, Select, MenuItem, Button, TextField, InputAdornment, Collapse } from '@material-ui/core';
import { renderSolrHighlightedResult, TextMaskCustom } from '../../../Common/CommonFunctions';
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import { ProductProperty2ProductProp } from '../../reducers/ProductsSearchRdc';
import ProductProperties from '../ProductProperties/ProductProperties';
import { PropertyFilter, CategoryFilter, UNSET_PROP_OPTION } from '../../../models/SearchFilter';
import { SupplierProductMapping, ProductMappingStatus, BoxingType } from '../../../api/models/SupplierProductMapping';
import PriceInput from '../../../Supplier/components/PriceInput';
import UpdateCatalogueRequest, { UpdateCatalogueOptionFilled } from '../../../Supplier/components/UpdateCatalogueRequest';


type Props = {
    taxonomy: TaxonomyElement,
    taxonomyLeafs: TaxonomyElement[],
    products: TaxonomyElement[],
    isBusy: boolean,
    highlights: { [id: string]: string[] },
    searchValue: string,
    pagination: React.ReactElement,
    onSaveChange: (spm: SupplierProductMapping) => void,
    mappingObj: SupplierProductMapping,
    isUpdateMappingBusy: boolean,
    isUpdateMappingSuccess: boolean | null,
    onMappingFinish: () => void,
    showPriceControls: boolean,
    sendCatUpdateProposition: (request: UpdateCatalogueOptionFilled) => void
}

type State = {
    selectedCatalogueProductId: string,
    selectedCatalogueProduct: TaxonomyElement | null,
    selectedBoxing: string,
    isPackage: boolean,
    boxingWeight: string,
    boxingWeightUnit: string,
    appliedPropertyFilters: PropertyFilter[],
    productProperties: { [id: string]: string },
    askingPrice: number,
    minimalPriceModifier: number,

    updateCatalogRequestOpen: boolean,

    validationError: boolean,
    boxingWeightValidationError: boolean,
    boxingTypeValidationError: boolean
}

const numberMask = createNumberMask({ prefix: '', includeThousandsSeparator: false, allowDecimal: true })

class SupplierProductList extends Component<Props, State> {
    constructor(props: Props) {
        super(props)


        let isPackage = this.props.mappingObj.boxingType === BoxingType.PACKAGE ? true : false
        let boxingWeight = this.props.mappingObj.boxingWeight > 0 ? this.props.mappingObj.boxingWeight.toString() : "0.00"
        this.state = {
            selectedCatalogueProductId: this.props.mappingObj.catalogueProductId || '',
            selectedCatalogueProduct: this.props.mappingObj.catalogueReference,
            selectedBoxing: this.props.mappingObj.boxingType || "",
            isPackage,
            boxingWeight,
            boxingWeightUnit: this.props.mappingObj.boxingWeightUnit || "KG",
            appliedPropertyFilters: this.toPropertyFilters(this.props.mappingObj.attributes),
            productProperties: this.props.mappingObj.attributes,
            askingPrice: this.props.mappingObj.askingPrice || 10,
            minimalPriceModifier: this.props.mappingObj.minimalPriceModifier || 1,
            validationError: false,
            boxingWeightValidationError: this.isBoxingWeightInvalid(boxingWeight, isPackage),
            boxingTypeValidationError: this.isBoxingTypeInvalid(this.props.mappingObj.boxingType, this.props.mappingObj.catalogueReference ? this.props.mappingObj.catalogueReference.units : []),
            updateCatalogRequestOpen: false
        }
    }

    toPropertyFilters = (attributes: { [name: string]: string }) => {
        if (attributes !== null) {
            let filters: PropertyFilter[] = []
            for (let att of Object.keys(attributes)) {
                filters.push(new PropertyFilter(attributes[att], att, attributes[att], "https://qmo.com/Facet/discrete"))
            }
            return filters;
        }
        else return []
    }

    componentDidMount() {
        if (this.props.mappingObj.catalogueReference === null && this.props.products.length > 0) {
            this.setState({
                selectedCatalogueProduct: this.props.products[0],
                selectedCatalogueProductId: this.props.products[0].id,
                boxingTypeValidationError: this.isBoxingTypeInvalid(this.state.selectedBoxing, this.props.products[0].units)
            })
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.isUpdateMappingBusy === true && this.props.isUpdateMappingBusy !== true && this.props.isUpdateMappingSuccess === true)
            this.props.onMappingFinish()

        if (prevProps.isBusy === true && this.props.isBusy !== true) {
            if (this.props.mappingObj.catalogueReference === null) {
                if (this.props.products.length > 0)
                    this.setState({
                        selectedCatalogueProduct: this.props.products[0],
                        selectedCatalogueProductId: this.props.products[0].id,
                        boxingTypeValidationError: this.isBoxingTypeInvalid(this.state.selectedBoxing, this.props.products[0].units)
                    })
                else
                    this.setState({ selectedCatalogueProduct: null, selectedCatalogueProductId: '' })
            }
        }
    }

    onPropertyChange = (cat?: CategoryFilter | null, property?: PropertyFilter | null) => {
        let appliedPropertyFilters = this.state.appliedPropertyFilters.slice()
        appliedPropertyFilters = appliedPropertyFilters.filter((f: PropertyFilter) => f.propId !== property!.propId)
        if (property!.id !== UNSET_PROP_OPTION)
            appliedPropertyFilters.push(property!)

        if (property && property!.value.toString() !== UNSET_PROP_OPTION) {
            let prop: { [id: string]: string } = {}
            prop[property!.propId] = property!.id.toString()
            this.setState({ productProperties: Object.assign({}, this.state.productProperties, prop), appliedPropertyFilters })
        }
        else {
            let prodProps = this.state.productProperties
            delete prodProps[property!.propId]
            this.setState({ productProperties: prodProps, appliedPropertyFilters })
        }

    }

    isBoxingWeightInvalid = (weight: string, isPackage: boolean): boolean => {
        if (!isPackage)
            return false

        if (weight === '' || parseFloat(weight) <= 0)
            return true
        else
            return false
    }

    isBoxingTypeInvalid = (boxingType: string | null, units: Unit[]): boolean => {
        if (boxingType === null)
            return true

        if (units.length > 0)
            return units.find(u => u.id === boxingType) === undefined
        else
            return true
    }

    onBoxingChange = (value: string) => {
        let selected = value
        let isPackage: boolean = selected === BoxingType.PACKAGE

        this.setState({
            selectedBoxing: selected, isPackage,
            boxingWeightValidationError: this.isBoxingWeightInvalid(this.state.boxingWeight, isPackage),
            boxingTypeValidationError: this.isBoxingTypeInvalid(selected, this.state.selectedCatalogueProduct!.units)
        });
    }

    mask = (props: any) => {
        return TextMaskCustom(props, numberMask)
    }

    render() {
        return (
            <div>
                {this.props.isBusy || !this.props.products ?
                    <div style={{ textAlign: 'center' }} >
                        <CircularProgress size={70} />
                    </div>
                    :

                    <Grid container alignItems="stretch" style={{ padding: '15px' }}>
                        <Grid item container sm={6}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">RESULTS</Typography>
                            </Grid>

                            <RadioGroup aria-label="opt" name="opt" value={this.state.selectedCatalogueProductId}
                                onChange={(e: any, v: string) => {
                                    let selectedProduct = this.props.products.find(p => p.id === v)!
                                    this.setState({
                                        selectedCatalogueProductId: v,
                                        selectedCatalogueProduct: selectedProduct,
                                    })
                                }
                                }>
                                {this.props.products.map((p, i) => {
                                    return (
                                        <FormControlLabel key={i} value={p.id} control={<Radio />} label={renderSolrHighlightedResult(this.props.searchValue, this.props.highlights[p.id], p.name)} />
                                    )
                                })}
                            </RadioGroup>
                            {this.props.products.length === 0 &&
                                <Typography variant="h3">No results found, try different phrase or contact us.</Typography>
                            }

                            {this.props.products.length > 0 &&
                                <Grid item xs={12} style={{ textAlign: 'left' }}>
                                    {this.props.pagination}
                                </Grid>
                            }
                        </Grid>
                        <Grid item container sm={6}>
                            <Grid item xs={12}>
                                {this.state.selectedCatalogueProduct &&
                                    <Typography variant="subtitle1">BOXING</Typography>
                                }

                                {this.state.selectedCatalogueProduct &&
                                    <Grid container alignItems="center">

                                        <Grid item xs={12} sm={6}>
                                            <FormControl style={{ margin: 0 }} >
                                                <InputLabel id="boxing-select-label">Sold per?</InputLabel>
                                                <Select
                                                    id="boxing-select"
                                                    labelId="boxing-select-label"
                                                    value={this.state.selectedBoxing}
                                                    onChange={(e) => this.onBoxingChange(e.target.value + '')}
                                                    style={{ minWidth: '100px' }}
                                                    name="unit"
                                                    error={this.state.boxingTypeValidationError}
                                                >
                                                    {this.state.selectedCatalogueProduct.units.map((u, i) => {
                                                        return <MenuItem key={i} value={u.id}>{u.name}</MenuItem>
                                                    })
                                                    }
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {this.state.isPackage &&
                                            <Grid item xs={12} sm={6}>
                                                <FormControl style={{ margin: 0 }} >
                                                    <TextField
                                                        style={{ margin: 0 }}
                                                        placeholder="0.00"
                                                        key={'3fdsdfew100'}
                                                        label="Package weight"
                                                        margin='normal'
                                                        value={this.state.boxingWeight}
                                                        onChange={(e) => {
                                                            this.setState({ boxingWeight: e.target.value, boxingWeightValidationError: this.isBoxingWeightInvalid(e.target.value, this.state.isPackage) })
                                                        }}
                                                        inputProps={{ style: { textAlign: 'center' }, }}
                                                        InputProps={{
                                                            inputComponent: this.mask,
                                                            endAdornment: <InputAdornment style={{ fontSize: '0.7em !important', textTransform: 'uppercase' }} position="end">{this.state.boxingWeightUnit}</InputAdornment>
                                                        }}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        error={this.state.boxingWeightValidationError}
                                                    />
                                                </FormControl>
                                            </Grid>
                                        }
                                    </Grid>
                                }

                                <Grid item xs={12} sm={12} style={{ height: '15px' }}></Grid>

                                <Grid item xs={12} sm={12}>
                                    {this.state.selectedCatalogueProduct &&
                                        <div>
                                            <Typography variant="subtitle1">PROPERTIES</Typography>
                                            <ProductProperties
                                                defaultValue={UNSET_PROP_OPTION}
                                                appliedPropertyFilters={this.state.appliedPropertyFilters}
                                                replaceFilter={this.onPropertyChange}
                                                properties={this.state.selectedCatalogueProduct.properties.map(pp => ProductProperty2ProductProp(pp, null))} />

                                            {this.props.showPriceControls &&
                                                <div><Typography variant="subtitle1">PRICING</Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item sm>
                                                            <PriceInput
                                                                value={this.state.askingPrice || 0}
                                                                label="Asking price"
                                                                disabled={false}
                                                                onLoad={(invalid: boolean) => { this.setState({ validationError: invalid }) }}
                                                                defaultCurrency='AU$'
                                                                onChange={(v: number, currency: string, invalid: boolean) => {
                                                                    let newMinimalPriceModifier = this.state.minimalPriceModifier ? this.state.minimalPriceModifier : 1
                                                                    this.setState({ askingPrice: v, minimalPriceModifier: newMinimalPriceModifier, validationError: invalid })
                                                                }}
                                                                currencies={[]} />
                                                        </Grid><Grid item sm>
                                                            <PriceInput
                                                                value={this.state.askingPrice * this.state.minimalPriceModifier}
                                                                label="Minimal price"
                                                                onLoad={(invalid: boolean) => { this.setState({ validationError: invalid }) }}

                                                                disabled={!this.state.askingPrice}
                                                                defaultCurrency='AU$'
                                                                onChange={(v: number, currency: string, invalid: boolean) => {
                                                                    this.setState({ minimalPriceModifier: v / this.state.askingPrice, validationError: invalid })
                                                                }}
                                                                lowerThan={this.state.askingPrice}
                                                                currencies={[]}
                                                                endAdornment={
                                                                    (() => {
                                                                        let discountString = ""
                                                                        if (this.state.minimalPriceModifier !== null && this.state.minimalPriceModifier !== undefined) {
                                                                            let discount = Math.round((1 - this.state.minimalPriceModifier) * 100) * -1
                                                                            discountString = (discount > 0 ? "+" + discount : discount) + "%"
                                                                        }
                                                                        return (<InputAdornment style={{ fontSize: '0.8rem', color: 'green' }} position="start">{discountString} </InputAdornment>)
                                                                    })()}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </div>
                                            }

                                            <Grid container spacing={2} alignContent="center" alignItems="center" style={{ textAlign: 'center', marginTop: '12px', marginBottom: '10px' }}>
                                                <Grid item sm>
                                                    <Typography style={{ cursor: 'pointer' }} onClick={() => this.setState({ updateCatalogRequestOpen: !this.state.updateCatalogRequestOpen })} variant="h3">Missing a product or property ?</Typography>
                                                    <Collapse
                                                        in={this.state.updateCatalogRequestOpen}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <UpdateCatalogueRequest onCancel={() => this.setState({ updateCatalogRequestOpen: false })} onSend={(request: UpdateCatalogueOptionFilled) => { this.props.sendCatUpdateProposition(request) }} />
                                                    </Collapse>
                                                </Grid>
                                            </Grid>
                                        </div>
                                    }
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12} style={{ position: 'relative', minHeight: "50px" }}>
                                {this.props.isUpdateMappingBusy &&
                                    <div style={{ position: 'absolute', left: 0, bottom: 0 }}>
                                        <CircularProgress size={30} />
                                    </div>
                                }

                                <div style={{ position: 'absolute', right: 0, bottom: 0 }}>
                                    {this.props.mappingObj._isMapped &&
                                        <Button
                                            style={{ position: 'relative' }}
                                            disabled={!this.props.mappingObj._isMapped || this.props.isUpdateMappingBusy}
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                let m = Object.assign({}, this.props.mappingObj)
                                                m.mappingStatus = ProductMappingStatus.NEW
                                                m.catalogueReference = null
                                                m.catalogueProductId = ""
                                                m.attributes = {}
                                                this.props.onSaveChange(m)
                                            }}
                                        >Unmap</Button>
                                    }

                                    <Button
                                        style={{ position: 'relative', marginLeft: '5px' }}
                                        disabled={this.state.selectedCatalogueProductId === ''
                                            || this.state.selectedCatalogueProductId === null
                                            || this.state.selectedBoxing === ''
                                            || this.state.boxingWeightValidationError
                                            || this.state.boxingTypeValidationError
                                            || this.props.isUpdateMappingBusy
                                            || this.state.validationError}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            let prodName = this.props.mappingObj.supplierProductName || this.state.selectedCatalogueProduct!.name
                                            let mapping: SupplierProductMapping = {
                                                ...this.props.mappingObj,
                                                catalogueProductId: this.state.selectedCatalogueProduct ? this.state.selectedCatalogueProduct.id : '',
                                                boxingType: this.state.selectedBoxing,
                                                boxingWeight: parseFloat(this.state.boxingWeight),
                                                boxingWeightUnit: this.state.boxingWeightUnit,
                                                //TODO: We should pass to FE also the SellableUnits property to pick from
                                                unit: this.state.selectedBoxing === 'https://qmo.com/core#perPackage' ? 'https://qmo.com/core#perKilo' : this.state.selectedBoxing,
                                                catalogueReference: this.state.selectedCatalogueProduct,
                                                attributes: this.state.appliedPropertyFilters.reduce((map: { [id: string]: string }, obj: PropertyFilter) => { map[obj.propId] = obj.id; return map }, {}),
                                                mappingStatus: ProductMappingStatus.CONFIRMED,
                                                supplierProductName: prodName,
                                                askingPrice: this.props.showPriceControls ? this.state.askingPrice : this.props.mappingObj.askingPrice,
                                                minimalPriceModifier: this.props.showPriceControls ? this.state.minimalPriceModifier : this.props.mappingObj.minimalPriceModifier
                                            }
                                            this.props.onSaveChange(mapping)
                                        }}>Save {this.props.showPriceControls ? "Offer" : "Mapping"}</Button>
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </div>
        );
    }
}

export default SupplierProductList;